/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { LemmonModal, useLocalization } from "@lemmonhq/uikit";
import type { Reservation, ReservationComment } from "@lemmonhq/types";
import { useToast } from "@app/vue/providers/Toast";
import { useTrpc } from "@app/vue/providers/TrpcProvider";

export interface ReservationCommentsProps {
  reservation: Reservation;
  onUpdate: (updatedReservation: Reservation) => void;
}

interface EditReservationCommentModalProps {
  comment: ReservationComment;
  saving: boolean;
  onClose: () => void;
  onSave: (message: string) => void;
}

const EditReservationCommentModal =
  defineComponent<EditReservationCommentModalProps>({
    name: "EditReservationCommentModal",
    props: ["comment", "saving", "onClose", "onSave"],
    setup(props) {
      const { t } = useLocalization();
      const message = ref(props.comment.message);

      const canSave = () =>
        Boolean(
          message.value.trim() &&
          message.value.trim() !== props.comment.message,
        );

      return () => (
        <LemmonModal
          title={t("reservations.details.edit_comment")}
          onClose={props.onClose}
          renderButtons={() => (
            <div class="p-4 border-t border-base-content/10">
              <div class="flex justify-between gap-2">
                <button
                  type="button"
                  class="btn btn-neutral"
                  onClick={props.onClose}
                  disabled={props.saving}
                >
                  {t("general.words.cancel")}
                </button>
                <button
                  type="button"
                  class="btn btn-primary"
                  onClick={() => props.onSave(message.value.trim())}
                  disabled={props.saving || !canSave()}
                >
                  {props.saving ? (
                    <span class="loading loading-spinner loading-xs" />
                  ) : null}
                  {t("general.words.save")}
                </button>
              </div>
            </div>
          )}
        >
          <div class="px-6 py-5">
            <label class="form-control">
              <span class="label-text text-xs font-semibold uppercase tracking-wider text-accent mb-2">
                {t("reservations.details.comment")}
              </span>
              <textarea
                class="textarea textarea-bordered min-h-28 text-sm"
                value={message.value}
                onInput={(e: any) => (message.value = e.target.value)}
                disabled={props.saving}
              />
            </label>
          </div>
        </LemmonModal>
      );
    },
  });

export const ReservationComments = defineComponent<ReservationCommentsProps>({
  name: "ReservationComments",
  props: ["reservation", "onUpdate"],
  setup(props) {
    const toast = useToast();
    const { t } = useLocalization();
    const trpc = useTrpc();
    const newComment = ref("");
    const isSubmittingComment = ref(false);
    const editingComment = ref<ReservationComment | null>(null);
    const isSavingComment = ref(false);

    const handleSubmitComment = async () => {
      if (!newComment.value.trim()) return;
      try {
        isSubmittingComment.value = true;
        const reservation = await trpc.reservations.createComment.mutate({
          reservation_id: props.reservation.id,
          message: newComment.value.trim(),
        });
        props.onUpdate(reservation as Reservation);
        newComment.value = "";
      } catch (e) {
        console.error("Failed to add comment:", e);
        toast.showToast(
          e instanceof Error
            ? e.message
            : t("reservations.details.comment_failed"),
          { type: "error" },
        );
      } finally {
        isSubmittingComment.value = false;
      }
    };

    const handleSaveComment = async (message: string) => {
      if (!editingComment.value || !message.trim()) return;
      try {
        isSavingComment.value = true;
        const reservation = await trpc.reservations.updateComment.mutate({
          reservation_id: props.reservation.id,
          comment_id: editingComment.value.id,
          message: message.trim(),
        });
        props.onUpdate(reservation as Reservation);
        editingComment.value = null;
      } catch (e) {
        console.error("Failed to update comment:", e);
        toast.showToast(
          e instanceof Error
            ? e.message
            : t("reservations.details.comment_update_failed"),
          {
            type: "error",
          },
        );
      } finally {
        isSavingComment.value = false;
      }
    };

    return () => {
      const comments = props.reservation.comments || [];

      return (
        <div class="p-2 bg-base-200 border rounded l-box-shadow">
          <h4 class="text-xs font-semibold text-accent uppercase tracking-wider mb-3">
            {t("reservations.details.internal_comments")}
          </h4>

          {comments.length > 0 && (
            <div class="space-y-1.5 mb-3">
              {comments.map((comment) => (
                <div
                  key={comment.id}
                  class="flex items-start gap-2 px-3 py-2 rounded-md bg-base-300"
                >
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center justify-between gap-2">
                      <span class="text-[11px] font-semibold text-amber-400">
                        {comment.user_name ||
                          t("reservations.details.unknown_user")}
                      </span>
                      <div class="flex items-center gap-1.5">
                        <span class="text-[10px] text-base-content/40">
                          {new Date(comment.created_at).toLocaleString(
                            undefined,
                            {
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            },
                          )}
                        </span>
                        <button
                          type="button"
                          class="btn btn-ghost btn-xs btn-square text-base-content/50 hover:text-primary"
                          onClick={() => (editingComment.value = comment)}
                          title={t("reservations.details.edit_comment")}
                          aria-label={t("reservations.details.edit_comment")}
                        >
                          <span class="material-symbols-outlined text-base">
                            edit_square
                          </span>
                        </button>
                      </div>
                    </div>
                    <p class="text-xs text-accent mt-0.5 whitespace-pre-wrap">
                      {comment.message}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div class="flex items-center gap-2">
            <input
              type="text"
              class="input input-bordered input-sm flex-1 text-xs"
              placeholder={t("reservations.details.add_comment_placeholder")}
              value={newComment.value}
              onInput={(e: any) => (newComment.value = e.target.value)}
              onKeydown={(e: KeyboardEvent) => {
                if (e.key === "Enter" && newComment.value.trim())
                  handleSubmitComment();
              }}
              disabled={isSubmittingComment.value}
            />
            <button
              onClick={handleSubmitComment}
              class="btn btn-sm btn-ghost text-primary"
              disabled={isSubmittingComment.value || !newComment.value.trim()}
            >
              {isSubmittingComment.value ? (
                <span class="loading loading-spinner loading-xs"></span>
              ) : (
                <span class="material-symbols-outlined text-lg">send</span>
              )}
            </button>
          </div>

          {editingComment.value && (
            <EditReservationCommentModal
              comment={editingComment.value}
              saving={isSavingComment.value}
              onClose={() => {
                if (!isSavingComment.value) editingComment.value = null;
              }}
              onSave={handleSaveComment}
            />
          )}
        </div>
      );
    };
  },
});
